import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Drawer, Select, Spin, Switch, Tag, Tooltip, Modal, Input, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import Gantt from "frappe-gantt";
import "./frappe-gantt.css";
import CapacityPlanner from "../components/CapacityPlanner";
import BaselineCompareModal from "../components/BaselineCompareModal";

import {
  fetchIssues,
  fetchDependencies,
  fetchBaselines,
  autoSchedule,
  getCriticalPath,
  updateIssue,
  createBaseline,
} from "../../../../core/data/redux/projects/thunks";
import {
  selectCurrentProject,
  selectIssuesByProject,
  selectIssuesLoading,
  selectDependencies,
  selectDependenciesLoading,
  selectBaselines,
  selectCriticalPath,
  selectSchedulingLoading,
  selectStatuses,
} from "../../../../core/data/redux/projects/selectors";
import { ISSUE_TYPES, STATUS_CATEGORY_COLORS } from "../../constants/projects.constants";
import { errMsg } from "../../helpers/errMsg.helper";
import { notify } from "../../../../services/toast.config";
import type { Issue, Dependency } from "../../../../core/data/redux/projects/types";
import type { AppDispatch } from "../../../../core/data/redux/store";

type ViewMode = "Day" | "Week" | "Month";

const VIEW_MODES: { value: ViewMode; label: string }[] = [
  { value: "Day", label: "Day" },
  { value: "Week", label: "Week" },
  { value: "Month", label: "Month" },
];

const GanttPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const project = useSelector(selectCurrentProject);
  const projectIssues = useSelector(selectIssuesByProject(project?.id ?? null));
  const issuesLoading = useSelector(selectIssuesLoading);
  const dependencies = useSelector(selectDependencies);
  const depsLoading = useSelector(selectDependenciesLoading);
  const baselines = useSelector(selectBaselines);
  const criticalPath = useSelector(selectCriticalPath);
  const schedulingLoading = useSelector(selectSchedulingLoading);
  const statuses = useSelector(selectStatuses);

  const ganttRef = useRef<HTMLDivElement>(null);
  const ganttInstanceRef = useRef<any>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("Week");
  const [showCriticalPath, setShowCriticalPath] = useState(false);
  const [baselineDrawerOpen, setBaselineDrawerOpen] = useState(false);
  const [newBaselineName, setNewBaselineName] = useState("");
  const [compareBaselineId, setCompareBaselineId] = useState<number | null>(null);

  // Fetch data on mount
  useEffect(() => {
    if (!project?.id) return;
    dispatch(fetchIssues({ project_id: project.id }));
    dispatch(fetchDependencies(project.id));
    dispatch(fetchBaselines(project.id));
  }, [dispatch, project?.id]);

  // Issues with dates for Gantt
  const ganttIssues = useMemo(() => {
    return (projectIssues || [])
      .filter((i) => i.is_active)
      .map((issue) => {
        const startDate = issue.start_date || issue.due_date || new Date().toISOString().split("T")[0];
        const endDate = issue.due_date || (issue.start_date ? addDays(issue.start_date, issue.planned_duration_days || 1) : addDays(startDate, 1));
        const typeMeta = ISSUE_TYPES.find((t) => t.value === issue.type);
        const status = statuses.find((s) => s.id === issue.status_id);
        const isCritical = showCriticalPath && criticalPath.some((cp: any) => (cp.issue_id || cp) === issue.id);

        return {
          id: String(issue.id),
          name: `${issue.issue_key} ${issue.title}`,
          start: startDate,
          end: endDate,
          progress: status?.category === "DONE" ? 100 : status?.category === "IN_PROGRESS" ? 50 : 0,
          dependencies: getDependencyIds(issue.id, dependencies),
          custom_class: isCritical ? "gantt-critical" : `gantt-type-${(issue.type || "TASK").toLowerCase()}`,
          _issue: issue,
          _color: isCritical ? "#ef4444" : typeMeta?.color || "#3b82f6",
        };
      });
  }, [projectIssues, dependencies, statuses, showCriticalPath, criticalPath]);

  useEffect(() => {
    if (!ganttRef.current || ganttIssues.length === 0) return;

    ganttRef.current.innerHTML = "";

    try {
      const gantt = new Gantt(ganttRef.current, ganttIssues, {
        view_mode: viewMode,
        date_format: "YYYY-MM-DD",
        bar_height: 28,
        bar_corner_radius: 6,
        padding: 14,
        on_date_change: (task: any, start: Date, end: Date) => {
          const issueId = Number(task.id);
          dispatch(updateIssue({
            issueId,
            payload: {
              start_date: start.toISOString().split("T")[0],
              due_date: end.toISOString().split("T")[0],
            },
          })).then(() => notify.success("Dates updated"));
        },
        on_progress_change: () => { },
        custom_popup_html: (task: any) => {
          const issue: Issue = task._issue;
          if (!issue) return "";
          const typeMeta = ISSUE_TYPES.find((t) => t.value === issue.type);
          const status = statuses.find((s) => s.id === issue.status_id);
          return `
            <div style="padding:12px 16px;min-width:220px;font-family:inherit">
              <div style="font-weight:600;font-size:14px;margin-bottom:6px">${issue.issue_key}</div>
              <div style="font-size:12.5px;color:#475569;margin-bottom:8px">${issue.title}</div>
              <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">
                ${typeMeta ? `<span style="background:${typeMeta.color}18;color:${typeMeta.color};padding:2px 8px;border-radius:10px;font-size:10px;font-weight:600">${typeMeta.label}</span>` : ""}
                ${status ? `<span style="background:${status.color || STATUS_CATEGORY_COLORS[status.category]};color:#fff;padding:2px 8px;border-radius:10px;font-size:10px">${status.name}</span>` : ""}
              </div>
              <div style="font-size:11px;color:#94a3b8;margin-top:6px">
                ${task.start ? new Date(task._start).toLocaleDateString() : "—"} → ${task.end ? new Date(task._end).toLocaleDateString() : "—"}
              </div>
            </div>
          `;
        },
      });
      ganttInstanceRef.current = gantt;
    } catch (e) {
      console.error("Gantt render error:", e);
    }
  }, [ganttIssues, dispatch, statuses]);

  useEffect(() => {
    const gantt = ganttInstanceRef.current;
    if (!gantt || typeof gantt.change_view_mode !== "function") return;
    try {
      gantt.change_view_mode(viewMode);
    } catch (e) {
      console.error("Gantt view mode change error:", e);
    }
  }, [viewMode]);

  // Handle auto-schedule
  const handleAutoSchedule = useCallback(async (force: boolean) => {
    if (!project?.id) return;
    try {
      await dispatch(autoSchedule({ project_id: project.id, force_reschedule: force })).unwrap();
      notify.success(force ? "All issues rescheduled" : "Missing dates filled");
      dispatch(fetchIssues({ project_id: project.id }));
    } catch (e: any) {
      notify.error(errMsg(e, "Auto-schedule failed"));
    }
  }, [dispatch, project?.id]);

  // Handle critical path
  const handleToggleCriticalPath = useCallback(async (checked: boolean) => {
    setShowCriticalPath(checked);
    if (checked && project?.id) {
      try {
        await dispatch(getCriticalPath(project.id)).unwrap();
      } catch (e: any) {
        notify.error(errMsg(e, "Failed to compute critical path"));
        setShowCriticalPath(false);
      }
    }
  }, [dispatch, project?.id]);

  // Handle create baseline
  const handleCreateBaseline = useCallback(async () => {
    if (!project?.id || !newBaselineName.trim()) return;
    try {
      await dispatch(createBaseline({ project_id: project.id, name: newBaselineName.trim() })).unwrap();
      notify.success("Baseline snapshot created");
      setNewBaselineName("");
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to create baseline"));
    }
  }, [dispatch, project?.id, newBaselineName]);

  if (!project) {
    return (
      <div className="card border-0" style={{ borderRadius: 14, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
        <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status" style={{ width: "2.5rem", height: "2.5rem" }} />
            <p className="mt-3 text-muted mb-0">Loading project…</p>
          </div>
        </div>
      </div>
    );
  }

  const loading = issuesLoading || depsLoading;

  return (
    <div>
      {/* Header */}
      <div className="card border-0 mb-3" style={{ borderRadius: 14, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
        <div className="card-body d-flex flex-wrap align-items-center gap-3">
          <div className="d-flex align-items-center gap-2" style={{ flexShrink: 0 }}>
            <div className="d-flex align-items-center justify-content-center" style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg, #3b82f6, #1d4ed8)" }}>
              <i className="ti ti-calendar-stats" style={{ color: "#fff", fontSize: 18 }} />
            </div>
            <div>
              <h5 className="mb-0" style={{ fontSize: 16, fontWeight: 600 }}>Gantt Chart</h5>
              <span className="text-muted" style={{ fontSize: 11.5 }}>{ganttIssues.length} issue{ganttIssues.length === 1 ? "" : "s"} • {dependencies.length} dependenc{dependencies.length === 1 ? "y" : "ies"}</span>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2 ms-auto flex-wrap">
            {/* View mode */}
            <Select
              size="small"
              value={viewMode}
              onChange={(v) => setViewMode(v as ViewMode)}
              options={VIEW_MODES}
              style={{ width: 100 }}
            />

            {/* Critical path toggle */}
            <Tooltip title="Highlight issues on the critical path">
              <div className="d-flex align-items-center gap-1" style={{ whiteSpace: "nowrap" }}>
                <Switch size="small" checked={showCriticalPath} onChange={handleToggleCriticalPath} />
                <span style={{ fontSize: 12, color: showCriticalPath ? "#ef4444" : "#64748b" }}>Critical Path</span>
              </div>
            </Tooltip>

            {/* Auto-schedule */}
            <Tooltip title="Fill missing start/end dates based on dependencies">
              <button
                type="button"
                className="btn btn-sm d-inline-flex align-items-center gap-1"
                style={{ borderRadius: 8, background: "linear-gradient(135deg, #10b981, #059669)", color: "#fff", whiteSpace: "nowrap", fontSize: 12, fontWeight: 600 }}
                onClick={() => handleAutoSchedule(false)}
                disabled={schedulingLoading}
              >
                {schedulingLoading ? <span className="spinner-border spinner-border-sm" /> : <i className="ti ti-calendar-event" style={{ fontSize: 14 }} />}
                <span>Auto Schedule</span>
              </button>
            </Tooltip>

            {/* Baselines */}
            <Tooltip title="View & create project baselines">
              <button
                type="button"
                className="btn btn-sm btn-light d-inline-flex align-items-center gap-1"
                style={{ borderRadius: 8, whiteSpace: "nowrap", fontSize: 12, fontWeight: 500 }}
                onClick={() => setBaselineDrawerOpen(true)}
              >
                <i className="ti ti-history" style={{ fontSize: 14, color: "#8b5cf6" }} />
                <span>Baselines</span>
                {baselines.length > 0 && (
                  <Tag style={{ margin: 0, fontSize: 9, lineHeight: "16px", borderRadius: 8 }}>{baselines.length}</Tag>
                )}
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Gantt Chart */}
      <div className="card border-0" style={{ borderRadius: 14, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
        {loading && ganttIssues.length === 0 ? (
          <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
            <div className="text-center">
              <Spin size="large" />
              <p className="mt-3 text-muted mb-0">Loading Gantt data…</p>
            </div>
          </div>
        ) : ganttIssues.length === 0 ? (
          <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
            <div className="text-center">
              <i className="ti ti-calendar-stats" style={{ fontSize: 48, color: "#cbd5e1" }} />
              <h5 className="mt-3 mb-2 fw-semibold">No Issues to Chart</h5>
              <p className="text-muted mb-0">Create issues with start/end dates to see them on the Gantt chart.</p>
            </div>
          </div>
        ) : (
          <div className="card-body p-0">
            <div ref={ganttRef} className="gantt-container" style={{ borderRadius: 14 }} />
          </div>
        )}
      </div>

      {/* Capacity Planner Heatmap */}
      {ganttIssues.length > 0 && project?.start_date && (
        <CapacityPlanner
          startDate={new Date(project.start_date)}
          endDate={new Date(Math.max(...ganttIssues.map((i) => new Date(i.end).getTime())))}
        />
      )}

      {/* Baselines Drawer */}
      <Drawer
        title="Project Baselines"
        open={baselineDrawerOpen}
        onClose={() => setBaselineDrawerOpen(false)}
        width={420}
        zIndex={1001}
      >
        <div className="mb-4">
          <label className="form-label small fw-semibold mb-1">Create New Baseline</label>
          <div className="d-flex gap-2">
            <Input
              placeholder="e.g. Sprint 3 Snapshot"
              value={newBaselineName}
              onChange={(e) => setNewBaselineName(e.target.value)}
              onPressEnter={handleCreateBaseline}
            />
            <button
              type="button"
              className="btn btn-info btn-sm d-inline-flex align-items-center gap-1"
              style={{ borderRadius: 8, whiteSpace: "nowrap" }}
              onClick={handleCreateBaseline}
              disabled={!newBaselineName.trim()}
            >
              <i className="ti ti-camera" style={{ fontSize: 14 }} />
              <span>Snapshot</span>
            </button>
          </div>
          <div className="text-muted mt-1" style={{ fontSize: 11 }}>
            Captures current start/end dates for all {ganttIssues.length} issues.
          </div>
        </div>

        {baselines.length === 0 ? (
          <div className="text-center py-4">
            <i className="ti ti-history" style={{ fontSize: 36, color: "#cbd5e1" }} />
            <p className="text-muted mt-2 mb-0">No baselines yet.</p>
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {baselines.map((b) => (
              <div
                key={b.id}
                className="d-flex align-items-center gap-3 px-3 py-2"
                style={{ borderRadius: 10, border: "1px solid #e2e8f0", transition: "all 0.2s" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.borderColor = "#c4b5fd"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "#e2e8f0"; }}
              >
                <div className="d-flex align-items-center justify-content-center" style={{ width: 32, height: 32, borderRadius: 8, background: "#f5f3ff", flexShrink: 0 }}>
                  <i className="ti ti-history" style={{ color: "#8b5cf6", fontSize: 16 }} />
                </div>
                <div className="flex-grow-1 min-width-0">
                  <div className="fw-semibold" style={{ fontSize: 13 }}>{b.name}</div>
                  <div className="text-muted" style={{ fontSize: 11 }}>
                    {new Date(b.snapshot_date).toLocaleDateString()} • {b.snapshot_count ?? "—"} issues
                  </div>
                </div>
                <button
                  type="button"
                  title="Compare"
                  onClick={() => setCompareBaselineId(b.id)}
                  className="btn btn-sm btn-success"
                >
                  Compare
                </button>
              </div>
            ))}
          </div>
        )}
      </Drawer>

      <BaselineCompareModal
        baselineId={compareBaselineId}
        projectId={project.id}
        open={compareBaselineId !== null}
        onClose={() => setCompareBaselineId(null)}
      />

      {/* Gantt Styles */}
      <style>{`
        .gantt-container .bar-wrapper .bar {
          rx: 6; ry: 6;
        }
        .gantt-container .gantt-critical .bar {
          fill: #ef4444 !important;
        }
        .gantt-container .gantt-critical .bar-progress {
          fill: #dc2626 !important;
        }
        .gantt-container .gantt-type-task .bar { fill: #3b82f6; }
        .gantt-container .gantt-type-bug .bar { fill: #ef4444; }
        .gantt-container .gantt-type-story .bar { fill: #10b981; }
        .gantt-container .gantt-type-epic .bar { fill: #8b5cf6; }
        .gantt-container .gantt-type-subtask .bar { fill: #f59e0b; }
        .gantt-container .bar-label { font-size: 11px; font-weight: 500; }
        .gantt-container .grid-header { fill: #f8fafc; }
        .gantt-container .grid-row { fill: #ffffff; }
        .gantt-container .grid-row:nth-child(even) { fill: #fafbfc; }
        .gantt-container .tick { stroke: #e2e8f0; }
        .gantt-container .today-highlight { fill: rgba(59, 130, 246, 0.06); }
        .gantt-container .arrow { stroke: #94a3b8; stroke-width: 1.5; }
        .gantt-container .popup-wrapper { background: white; border-radius: 12px; box-shadow: 0 8px 32px rgba(0,0,0,0.12); border: 1px solid #e2e8f0; }
      `}</style>
    </div>
  );
};

/* ─── Helpers ─── */

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

function getDependencyIds(issueId: number, deps: Dependency[]): string {
  return deps
    .filter((d) => Number(d.successor_id) === issueId)
    .map((d) => String(d.predecessor_id))
    .join(", ");
}

export default GanttPage;
